import { supabase } from '@/integrations/supabase/client';
import { Procurement, CreateProcurementInput } from '@/types/database.types';
import { enrichProcurementWithDetails } from './procurement/enrichmentService';

export const getProcurements = async (currentUserId?: string, userRole?: string): Promise<Procurement[]> => {
  console.log('Fetching procurements for user:', currentUserId, 'with role:', userRole);
  
  let query = supabase
    .from('procurements')
    .select('*')
    .order('created_at', { ascending: false });

  // Filter by current user for sellers
  if (currentUserId && userRole === 'seller') {
    query = query.eq('requestor_id', currentUserId);
    console.log('Filtering procurements for seller:', currentUserId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching procurements:', error);
    throw error;
  }

  console.log('Raw procurements data:', data?.length || 0);

  // Enrich with detailed data
  const enrichedProcurements = await Promise.all(
    data.map(async (procurement) => {
      const enriched = await enrichProcurementWithDetails(procurement);
      return enriched;
    })
  );

  console.log('Final enriched procurements:', enrichedProcurements.length);
  return enrichedProcurements;
};

export const getCurrentProcurement = async (): Promise<Procurement[]> => {
  const { data, error } = await supabase
    .from('procurements')
    .select('*')
    .limit(1)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching current procurement:', error);
    throw error;
  }

  if (!data) return [];

  // Enrich with detailed data
  const enrichedProcurements = await Promise.all(
    data.map(async (procurement) => {
      const enriched = await enrichProcurementWithDetails(procurement);
      return enriched;
    })
  );

  return enrichedProcurements;
};

export const getProcurementByHarvestId = async (harvestId: string): Promise<Procurement | null> => {
  const { data, error } = await supabase
    .from('procurements')
    .select('*')
    .eq('harvest_id', harvestId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching procurement by harvest ID:', error);
    throw error;
  }

  if (!data) return null;

  // Enrich with detailed data
  const enriched = await enrichProcurementWithDetails(data);
  return enriched;
};

export const getProcurementById = async (id: string): Promise<Procurement | null> => {
  console.log(`Attempting to fetch procurement with ID: ${id}`);
  
  const { data, error } = await supabase
    .from('procurements')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching procurement by ID:', error);
    throw error;
  }

  if (!data) {
    console.log(`No procurement found with ID: ${id}`);
    return null;
  }

  console.log(`Successfully fetched procurement with ID: ${id}`);

  // Enrich with detailed data
  const enriched = await enrichProcurementWithDetails(data);
  return enriched;
};

export const createProcurement = async (data: CreateProcurementInput): Promise<Procurement> => {
  // First, create the main procurement record
  const { data: procurementData, error: procurementError } = await supabase
    .from('procurements')
    .insert([{
      harvest_id: data.harvest_id,
      total_quantity: data.total_quantity,
      total_amount: data.total_amount,
      expected_delivery_date: data.expected_delivery_date,
      delivery_date: data.delivery_date,
      source_id: data.source_id,
      requestor_id: data.requestor_id,
      status: data.status,
      quality_grade: data.quality_grade,
      notes: data.notes,
      source_type: data.source_type,
      product_name: data.product_name,
      rejection_source: data.rejection_source,
      rejected_by: data.rejected_by
    }])
    .select()
    .single();

  if (procurementError) {
    console.error('Error creating procurement:', procurementError);
    throw procurementError;
  }

  if (!procurementData) {
    throw new Error('Failed to create procurement');
  }

  const procurementId = procurementData.id;

  // Next, create related records if they exist
  if (data.items && data.items.length > 0) {
    const itemsToInsert = data.items.map(item => ({
      ...item,
      procurement_id: procurementId,
    }));

    const { error: itemsError } = await supabase
      .from('procurement_items')
      .insert(itemsToInsert);

    if (itemsError) {
      console.error('Error creating procurement items:', itemsError);
      throw itemsError;
    }
  }

  // Create costs record if it exists
  if (data.costs) {
    const { error: costsError } = await supabase
      .from('procurement_costs')
      .insert([{
        ...data.costs,
        procurement_id: procurementId,
      }]);

    if (costsError) {
      console.error('Error creating procurement costs:', costsError);
      throw costsError;
    }
  }

  // Create finance record if it exists
  if (data.finance) {
    const { error: financeError } = await supabase
      .from('procurement_finance')
      .insert([{
        ...data.finance,
        procurement_id: procurementId,
      }]);

    if (financeError) {
      console.error('Error creating procurement finance:', financeError);
      throw financeError;
    }
  }

  return procurementData as Procurement;
};

export const updateProcurementStatus = async (id: string, status: string, changeReason?: string, changedBy?: string): Promise<Procurement> => {
  // Fetch the existing procurement record
  const { data: existingData, error: existingError } = await supabase
    .from('procurements')
    .select('*')
    .eq('id', id)
    .single();

  if (existingError) {
    console.error('Error fetching existing procurement:', existingError);
    throw existingError;
  }

  if (!existingData) {
    throw new Error('Procurement not found');
  }

  // Prepare audit log entry
  const auditLogEntry = {
    procurement_id: id,
    action: 'status_change',
    old_status: existingData.status,
    new_status: status,
    change_reason: changeReason || 'No reason provided',
    changed_by: changedBy || 'System',
  };

  // Update the procurement record
  const { data: updatedData, error: updateError } = await supabase
    .from('procurements')
    .update({ status })
    .eq('id', id)
    .select()
    .single();

  if (updateError) {
    console.error('Error updating procurement status:', updateError);
    throw updateError;
  }

  if (!updatedData) {
    throw new Error('Failed to update procurement status');
  }

  // Insert audit log entry
  const { error: auditError } = await supabase
    .from('procurement_audit')
    .insert([auditLogEntry]);

  if (auditError) {
    console.error('Error creating audit log:', auditError);
    // Consider whether to throw an error or just log it
  }

  return updatedData as Procurement;
};

export const deleteProcurement = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('procurements')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting procurement:', error);
    throw error;
  }
};
